import { Vector3, MeshBuilder, StandardMaterial, Color3, Mesh, DynamicTexture } from '@babylonjs/core';
import { loadModel, playAnimation } from './ModelLoader';
const FACTION_COLORS = {
    sword: { primary: new Color3(0.15, 0.45, 0.85), accent: new Color3(0.6, 0.8, 1.0) },
    dao: { primary: new Color3(0.55, 0.2, 0.75), accent: new Color3(0.8, 0.5, 1.0) },
    monk: { primary: new Color3(0.85, 0.6, 0.08), accent: new Color3(1.0, 0.85, 0.3) },
    shadow: { primary: new Color3(0.2, 0.2, 0.25), accent: new Color3(0.4, 0.4, 0.45) },
};
export class RemotePlayerEntity {
    mesh;
    name;
    faction;
    hp;
    maxHp;
    targetPos;
    targetRotY = 0;
    nameplate;
    hpBar;
    hpFill;
    glbModel = null;
    currentAnim = 'idle';
    lastPos;
    constructor(scene, data) {
        this.name = data.name || '???';
        this.faction = data.faction || 'sword';
        this.hp = data.hp;
        this.maxHp = data.maxHp;
        this.targetPos = new Vector3(data.x, 0, data.z);
        this.targetRotY = data.rotationY;
        this.mesh = new Mesh('remote_player', scene);
        // 构建几何体角色
        this.buildPrimitiveModel(scene);
        // 名字板
        this.nameplate = this.createNameplate(scene, this.name, data.sinValue > 0);
        this.nameplate.parent = this.mesh;
        this.nameplate.position.y = 2.4;
        this.nameplate.billboardMode = Mesh.BILLBOARDMODE_ALL;
        // 血条 (背景)
        this.hpBar = MeshBuilder.CreatePlane('hpBarBg', { width: 1.2, height: 0.1 }, scene);
        this.hpBar.position.y = 2.2;
        this.hpBar.parent = this.mesh;
        this.hpBar.billboardMode = Mesh.BILLBOARDMODE_ALL;
        const hpBarMat = new StandardMaterial('hpBarBgMat', scene);
        hpBarMat.diffuseColor = new Color3(0.2, 0.2, 0.2);
        hpBarMat.emissiveColor = new Color3(0.1, 0.1, 0.1);
        this.hpBar.material = hpBarMat;
        // 血条 (填充)
        this.hpFill = MeshBuilder.CreatePlane('hpBarFill', { width: 1.18, height: 0.08 }, scene);
        this.hpFill.position.y = 2.2;
        this.hpFill.position.z = -0.01;
        this.hpFill.parent = this.mesh;
        this.hpFill.billboardMode = Mesh.BILLBOARDMODE_ALL;
        const hpFillMat = new StandardMaterial('hpFillMat', scene);
        hpFillMat.diffuseColor = new Color3(0.1, 0.8, 0.2);
        hpFillMat.emissiveColor = new Color3(0.05, 0.4, 0.1);
        this.hpFill.material = hpFillMat;
        this.mesh.position = this.targetPos.clone();
        this.lastPos = this.mesh.position.clone();
        // 异步加载 glb
        this.tryLoadGLB(scene);
    }
    buildPrimitiveModel(scene) {
        const colors = FACTION_COLORS[this.faction] || { primary: new Color3(0.5, 0.5, 0.5), accent: new Color3(0.7, 0.7, 0.7) };
        // 腿
        const legMat = new StandardMaterial('rlegMat', scene);
        legMat.diffuseColor = new Color3(0.25, 0.22, 0.2);
        const leftLeg = MeshBuilder.CreateCylinder('rlleg', { height: 0.65, diameterTop: 0.14, diameterBottom: 0.16, tessellation: 8 }, scene);
        leftLeg.position = new Vector3(-0.11, 0.33, 0);
        leftLeg.parent = this.mesh;
        leftLeg.material = legMat;
        const rightLeg = leftLeg.clone('rrleg');
        rightLeg.position.x = 0.11;
        rightLeg.parent = this.mesh;
        // 靴子
        const bootMat = new StandardMaterial('rbootMat', scene);
        bootMat.diffuseColor = new Color3(0.2, 0.15, 0.1);
        const leftBoot = MeshBuilder.CreateBox('rlboot', { width: 0.16, height: 0.07, depth: 0.22 }, scene);
        leftBoot.position = new Vector3(-0.11, 0.035, 0.03);
        leftBoot.parent = this.mesh;
        leftBoot.material = bootMat;
        const rightBoot = leftBoot.clone('rrboot');
        rightBoot.position.x = 0.11;
        rightBoot.parent = this.mesh;
        // 身体
        const body = MeshBuilder.CreateCylinder('rbody', {
            height: 0.85, diameterTop: 0.38, diameterBottom: 0.5, tessellation: 10
        }, scene);
        body.position.y = 1.0;
        body.parent = this.mesh;
        const bodyMat = new StandardMaterial('rbodyMat', scene);
        bodyMat.diffuseColor = colors.primary;
        body.material = bodyMat;
        // 腰带
        const belt = MeshBuilder.CreateTorus('rbelt', { diameter: 0.45, thickness: 0.05, tessellation: 14 }, scene);
        belt.position.y = 0.62;
        belt.parent = this.mesh;
        const beltMat = new StandardMaterial('rbeltMat', scene);
        beltMat.diffuseColor = colors.accent;
        beltMat.emissiveColor = colors.accent.scale(0.1);
        belt.material = beltMat;
        // 肩甲
        const shoulderMat = new StandardMaterial('rshoulderMat', scene);
        shoulderMat.diffuseColor = colors.accent;
        shoulderMat.emissiveColor = colors.accent.scale(0.1);
        const ls = MeshBuilder.CreateSphere('rlsh', { diameter: 0.2, segments: 6 }, scene);
        ls.position = new Vector3(-0.28, 1.38, 0);
        ls.parent = this.mesh;
        ls.material = shoulderMat;
        const rs = ls.clone('rrsh');
        rs.position.x = 0.28;
        rs.parent = this.mesh;
        // 手臂
        const la = MeshBuilder.CreateCylinder('rlarm', { height: 0.5, diameter: 0.09, tessellation: 6 }, scene);
        la.position = new Vector3(-0.28, 1.1, 0);
        la.parent = this.mesh;
        la.material = bodyMat;
        const ra = la.clone('rrarm');
        ra.position.x = 0.28;
        ra.parent = this.mesh;
        // 脖子 + 头
        const skinMat = new StandardMaterial('rskinMat', scene);
        skinMat.diffuseColor = new Color3(0.9, 0.75, 0.6);
        const neck = MeshBuilder.CreateCylinder('rneck', { height: 0.1, diameter: 0.14, tessellation: 6 }, scene);
        neck.position.y = 1.48;
        neck.parent = this.mesh;
        neck.material = skinMat;
        const head = MeshBuilder.CreateSphere('rhead', { diameter: 0.38, segments: 10 }, scene);
        head.position.y = 1.75;
        head.parent = this.mesh;
        head.material = skinMat;
        // 头发
        const hairMat = new StandardMaterial('rhairMat', scene);
        hairMat.diffuseColor = new Color3(0.15, 0.1, 0.08);
        const hair = MeshBuilder.CreateSphere('rhair', { diameter: 0.4, segments: 8 }, scene);
        hair.position.y = 1.8;
        hair.position.z = -0.02;
        hair.scaling = new Vector3(1, 0.85, 1.08);
        hair.parent = this.mesh;
        hair.material = hairMat;
    }
    createNameplate(scene, name, isRed) {
        const plane = MeshBuilder.CreatePlane('nameplate', { width: 2, height: 0.3 }, scene);
        const texture = new DynamicTexture('nameTexture', { width: 256, height: 40 }, scene, false);
        texture.hasAlpha = true;
        const ctx = texture.getContext();
        ctx.clearRect(0, 0, 256, 40);
        ctx.font = 'bold 24px Microsoft YaHei';
        ctx.textAlign = 'center';
        ctx.fillStyle = isRed ? '#ff4444' : '#ffffff';
        ctx.fillText(name, 128, 30);
        texture.update();
        const mat = new StandardMaterial('nameplateMat', scene);
        mat.diffuseTexture = texture;
        mat.emissiveColor = new Color3(1, 1, 1);
        mat.disableLighting = true;
        mat.backFaceCulling = false;
        plane.material = mat;
        return plane;
    }
    async tryLoadGLB(scene) {
        const model = await loadModel(scene, `/models/${this.faction}_character.glb`);
        if (model) {
            // 移除所有几何体子节点（保留 nameplate, hpBar, hpFill）
            this.mesh.getChildMeshes().forEach(m => {
                if (m !== this.nameplate && m !== this.hpBar && m !== this.hpFill) {
                    m.dispose();
                }
            });
            model.root.parent = this.mesh;
            model.root.scaling.setAll(0.01);
            this.glbModel = model;
        }
    }
    updateFromServer(data) {
        this.targetPos.set(data.x, 0, data.z);
        this.targetRotY = data.rotationY;
        this.hp = data.hp;
        this.maxHp = data.maxHp;
        // 更新血条
        const ratio = Math.max(0, this.hp / this.maxHp);
        this.hpFill.scaling.x = ratio;
        this.hpFill.position.x = -(1 - ratio) * 0.59;
        // 血条颜色随血量变化
        const fillMat = this.hpFill.material;
        if (ratio > 0.5) {
            fillMat.diffuseColor = new Color3(0.1, 0.8, 0.2);
        }
        else if (ratio > 0.25) {
            fillMat.diffuseColor = new Color3(0.9, 0.7, 0.1);
        }
        else {
            fillMat.diffuseColor = new Color3(0.9, 0.15, 0.1);
        }
        // 死亡
        if (data.isDead) {
            this.mesh.scaling = new Vector3(1, 0.2, 1);
        }
        else {
            this.mesh.scaling = Vector3.One();
        }
    }
    interpolate() {
        this.lastPos.copyFrom(this.mesh.position);
        this.mesh.position = Vector3.Lerp(this.mesh.position, this.targetPos, 0.15);
        this.mesh.rotation.y += (this.targetRotY - this.mesh.rotation.y) * 0.15;
        // 简易行走动画
        const moved = Vector3.Distance(this.mesh.position, this.lastPos) > 0.01;
        if (!this.glbModel) {
            if (moved) {
                const t = performance.now() * 0.008;
                const swing = Math.sin(t) * 0.25;
                const children = this.mesh.getChildMeshes();
                const ll = children.find(m => m.name === 'rlleg');
                const rl = children.find(m => m.name === 'rrleg');
                if (ll)
                    ll.rotation.x = swing;
                if (rl)
                    rl.rotation.x = -swing;
                const la = children.find(m => m.name === 'rlarm');
                const ra = children.find(m => m.name === 'rrarm');
                if (la)
                    la.rotation.x = -swing * 0.6;
                if (ra)
                    ra.rotation.x = swing * 0.6;
            }
            else {
                // 还原
                const children = this.mesh.getChildMeshes();
                for (const m of children) {
                    if (['rlleg', 'rrleg', 'rlarm', 'rrarm'].includes(m.name)) {
                        m.rotation.x *= 0.85;
                    }
                }
            }
        }
        else {
            const anim = moved ? 'run' : 'idle';
            if (this.currentAnim !== anim) {
                playAnimation(this.glbModel.animations, anim);
                this.currentAnim = anim;
            }
        }
    }
    dispose() {
        this.mesh.dispose(false, true);
    }
}
